import apiService, { ServiceType } from './api';

/**
 * Read-only view of applied corporate actions (BE `/corporate-actions/recent`).
 *
 * Recording and applying them is admin work in a different app. This exists so a config whose
 * numbers were rewritten by an ex-rights adjustment can SAY so: the user did not type
 * `grid_origin = 23.8`, and a screen that presents it as if they had is quietly wrong about
 * whose number it is.
 */

export type CorporateActionType =
  | 'CASH_DIVIDEND'
  | 'STOCK_DIVIDEND'
  | 'BONUS_ISSUE'
  | 'RIGHTS_ISSUE'
  | 'STOCK_SPLIT'
  | 'OTHER';

/** Event kind in words. The enum name is a database detail, not something to show an owner. */
export const CORPORATE_ACTION_TYPE_LABELS: Record<CorporateActionType, string> = {
  CASH_DIVIDEND: 'cash dividend',
  STOCK_DIVIDEND: 'stock dividend',
  BONUS_ISSUE: 'bonus issue',
  RIGHTS_ISSUE: 'rights issue',
  STOCK_SPLIT: 'stock split',
  OTHER: 'corporate action',
};

export interface AppliedCorporateAction {
  id: string;
  exchange: string;
  symbol: string;
  /** Ex-rights date, `YYYY-MM-DD`. */
  exDate: string;
  type: CorporateActionType;
  /** `f` — every price in the config was multiplied by this. */
  priceFactor: number | null;
  /** `s` — the share count was multiplied by this. 1 when no new shares were issued. */
  shareFactor: number | null;
  /** RAW VND per share. */
  cashDividend: number | null;
  appliedAt: string | null;
}

const core = () => apiService.getService(ServiceType.CORE);

export async function getRecentCorporateActions(
  symbols: string[],
  withinDays = 30,
): Promise<AppliedCorporateAction[]> {
  const list = symbols.map((s) => s.trim().toUpperCase()).filter(Boolean);
  if (list.length === 0) return [];
  const { data } = await core().get<AppliedCorporateAction[]>('/corporate-actions/recent', {
    params: { symbols: list.join(','), withinDays },
  });
  return data;
}

/** One adjustment as applied to ONE config — the unit Revert works on. */
export interface CorporateActionApplication {
  id: string;
  groupId: string;
  symbol: string;
  exDate: string;
  dataConfigId: string;
  status: 'APPLIED' | 'REVERTED';
  priceFactor: number | null;
  shareFactor: number | null;
  cashPerShare: number | null;
  revenueRatio: number | null;
  appliedAt: string;
  appliedBy: string;
  revertedAt: string | null;
  revertedBy: string | null;
  /** The engine has folded this into its own book — position count and dividend ledger. */
  foldedByBot: boolean;
  /**
   * State, not permission: false once the bot has folded the factors in, which is when only a
   * corrected factor can fix a mistake. Reverting itself is admin work.
   */
  revertable: boolean;
}

export interface PendingCorporateAction {
  symbol: string;
  exDate: string;
  type: CorporateActionType;
  /** RAW VND per share. CASH_DIVIDEND. */
  cashDividend: number | null;
  /** New shares per existing share. STOCK_DIVIDEND / BONUS_ISSUE / RIGHTS_ISSUE. */
  ratioNew: number | null;
  /** RAW VND. RIGHTS_ISSUE. */
  subscriptionPrice: number | null;
  /** Shares after / before. STOCK_SPLIT. */
  splitFactor: number | null;
  status: string;
  reason: string | null;
}

/**
 * An event past its ex-date that this config is NOT being held down for, because its wallet
 * is newer than the event.
 *
 * The default reading is that such a config was already priced in the re-based unit. That is a
 * guess about how the numbers were typed, so it is SHOWN rather than hidden — but resolving it
 * is admin work like every other adjustment, so this is a notice, not an offer.
 */
export interface NotOwedCorporateAction {
  symbol: string;
  exDate: string;
  status: string;
  walletCreatedAt: string | null;
}

export interface ConfigCorporateActionStatus {
  dataConfigId: string;
  /** Who adjusts. Always 'ADMIN': an ex-rights date is not a per-account choice. */
  appliedBy: 'ADMIN';
  /** Platform policy — whether an admin reviews first (REVIEW) or the 08:15 sweep does it. */
  mode: 'REVIEW' | 'AUTO';
  /** Why the bot will not start. Empty means nothing is owed.  */
  pending: PendingCorporateAction[];
  /** Past events the default reading says this config does not owe. Adjustable on request. */
  notOwed: NotOwedCorporateAction[];
  applied: CorporateActionApplication[];
}

export async function getConfigCorporateActions(
  dataConfigId: string,
): Promise<ConfigCorporateActionStatus> {
  const { data } = await core().get<ConfigCorporateActionStatus>(
    `/corporate-actions/config/${dataConfigId}`,
  );
  return data;
}

export async function previewConfigCorporateAction(
  dataConfigId: string,
  exchange: string,
  symbol: string,
  exDate: string,
): Promise<unknown> {
  const { data } = await core().get(`/corporate-actions/config/${dataConfigId}/preview`, {
    params: { exchange, symbol, exDate },
  });
  return data;
}

/**
 * Everything above is READ ONLY on purpose.
 *
 * Applying, reverting and correcting an adjustment live in the admin app. An ex-rights date is
 * a fact about the market — the same fact for every account holding the symbol — and a config
 * still priced in the pre-event unit is wrong rather than differently configured. There is no
 * per-account decision to expose, and offering one implied a choice that does not exist.
 *
 * (Contrast the futures roll: which contract to hold, and when, genuinely is the owner's call.)
 */
